import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { CalloutSize } from '@freelancer/ui/callout';
import { TextAlign } from '@freelancer/ui/text';
import type { TableColumnGridWidth } from './table.types';
import {
  ResponsiveColumnPlacement,
  TableColumnVerticalAlign,
} from './table.types';

interface CellContext<T> {
  $implicit: T;
  index: number;
}

@Component({
  standalone: true,
  selector: 'fl-table-col',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnComponent<T> implements OnChanges, OnInit {
  private elementRef = inject(ElementRef);

  /** String title for the column. Must be unique in table. */
  @Input() titleText: string;
  @Input() columnName: string;
  @Input() textAlign = TextAlign.LEFT;
  @Input() verticalAlign = TableColumnVerticalAlign.MIDDLE;
  @Input() columnPlacement: ResponsiveColumnPlacement =
    ResponsiveColumnPlacement.HIDE_COLUMN;
  @Input() gridWidth: TableColumnGridWidth | undefined = undefined;
  @Input() sortable = false;
  @Input() tooltipIconName: string;
  @Input() headerNoWrap = false;
  @Input() calloutSize = CalloutSize.MEDIUM;
  @Output() sortClicked = new EventEmitter<void>();

  @ContentChild('cell') cellTemplate: TemplateRef<CellContext<T>>;
  @ContentChild('calloutContent')
  calloutContent: TemplateRef<TableColumnComponent<T>>;

  // Internal properties set from CSS classes on the host element
  // These are read by the table component to apply classes to rendered cells
  hide = false;
  hideMobile = false;
  hideTablet = false;
  hideDesktop = false;
  ngOnInit(): void {
    this.updateHideProperties();
  }

  ngOnChanges(): void {
    if (this.titleText && !this.columnName) {
      this.columnName = this.titleText;
    }
    this.updateHideProperties();
  }

  private updateHideProperties(): void {
    // Check for CSS classes on the host element and set internal properties
    // This allows class="desktop:hide mobile:hide tablet:hide" to work
    if (!this.elementRef?.nativeElement) {
      return;
    }

    const { classList } = this.elementRef.nativeElement;

    if (classList.contains('desktop:hide')) {
      this.hideDesktop = true;
    }
    if (classList.contains('mobile:hide')) {
      this.hideMobile = true;
    }
    if (classList.contains('tablet:hide')) {
      this.hideTablet = true;
    }
    if (classList.contains('hide')) {
      this.hide = true;
    }
  }
}
